import React from 'react';
import { DetailSection } from '../common';
import { format, parseISO } from 'date-fns';
import {
    FaPlane,
    FaTicketAlt,
    FaCalendarAlt,
    FaClock,
    FaGlobe,
    FaUser,
    FaSuitcase,
    FaArrowRight
} from 'react-icons/fa';
import { calculateFlightDuration } from '@trip-planner-monorepo/shared/utils/timeUtils';

/**
 * Flight-specific details display
 */
const FlightDetails = ({ travel, tripId, RelatedDocumentsSection, onUnlink }) => {
    const { details, cost } = travel;

    if (!details) {
        return <div className="error-message">No flight details available</div>;
    }

    // Format date for display
    const formatDateTime = (dateStr) => {
        if (!dateStr) return { date: 'Not specified', time: '' };

        try {
            const date = parseISO(dateStr);
            return {
                date: format(date, 'MMM d, yyyy'),
                time: format(date, 'h:mm a')
            };
        } catch (e) {
            return { date: dateStr, time: '' };
        }
    };

    // Format airport info
    const getAirportInfo = (code, city) => {
        if (!code) return { code: 'N/A', city: city || 'No city specified', fullDisplay: 'No airport specified' };

        // Map of common airport codes to proper names
        const airportNames = {
            'NRT': 'Narita International',
            'HND': 'Haneda International',
            'ORY': 'Orly',
            'CDG': 'Charles de Gaulle',
            'LHR': 'Heathrow',
            'LGW': 'Gatwick',
            'JFK': 'John F. Kennedy',
            'LAX': 'Los Angeles International',
            'SFO': 'San Francisco International',
            'ORD': 'O\'Hare International',
            'DFW': 'Dallas/Fort Worth',
            'SIN': 'Changi',
            'HKG': 'Hong Kong International',
            'DXB': 'Dubai International',
            'AMS': 'Schiphol',
            'FRA': 'Frankfurt',
            'MAD': 'Madrid-Barajas',
            'BCN': 'Barcelona-El Prat',
            'SYD': 'Sydney Kingsford Smith',
            'MEL': 'Melbourne'
        };

        const upperCode = code.toUpperCase();
        // Get the proper airport name or use the code if not found
        const airportName = airportNames[upperCode] || upperCode;

        return {
            code: upperCode,
            city: city || code,
            fullDisplay: `${airportName} (${upperCode})`
        };
    };

    // Calculate flight duration
    const calculateDuration = () => {
        if (!details.departureTime || !details.arrivalTime) return null;

        try {
            // Use our utility function with timezone information
            const durationResult = calculateFlightDuration(
                details.departureTime,
                details.arrivalTime,
                details.departureTimezone || 'Asia/Tokyo', // Default to Tokyo timezone if not specified
                details.arrivalTimezone || 'Europe/Paris'  // Default to Paris timezone if not specified
            );

            return durationResult.formatted || null;
        } catch (e) {
            console.error('Error calculating flight duration:', e);
            return null;
        }
    };

    // Format currency
    const formatCurrency = (amount, currency = 'USD') => {
        if (amount === undefined || amount === null) return 'Not specified';

        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(amount);
        } catch (e) {
            return `${amount} ${currency}`;
        }
    };

    // Format departure and arrival information
    const departure = {
        airport: getAirportInfo(details.departureAirport, details.departureCity),
        datetime: formatDateTime(details.departureTime),
        timezone: details.departureTimezone || 'Local time'
    };

    const arrival = {
        airport: getAirportInfo(details.arrivalAirport, details.arrivalCity),
        datetime: formatDateTime(details.arrivalTime),
        timezone: details.arrivalTimezone || 'Local time'
    };

    const duration = calculateDuration();
    const hasPassengers = Array.isArray(details.passengers) && details.passengers.length > 0;

    return (
        <div className="flight-details">
            {/* Entity Connections - Flight Route and Related Documents */}
            <div className="entity-connections">
                {/* Flight Route Card */}
                <div className="flight-route-card">
                    <div className="flight-route">
                        {/* Departure Information */}
                        <div className="departure-info">
                            <div className="city-name">{departure.airport.city}</div>
                            <div className="airport-code">{departure.airport.fullDisplay}</div>
                            <div className="time">{departure.datetime.time}</div>
                            <div className="date">{departure.datetime.date === 'Not specified' ? 'No departure date' : departure.datetime.date}</div>
                            <div className="timezone">{departure.timezone}</div>
                        </div>

                        {/* Flight Divider with Flight Info */}
                        <div className="flight-divider">
                            <div className="airline-logo">
                                <FaPlane /> <span className="airline-code">{details.airline}</span>
                            </div>

                            {duration && (
                                <div className="flight-duration">
                                    <FaClock /> {duration}
                                </div>
                            )}

                            <div className="flight-line"></div>

                            <div className="flight-number">
                                Flight {details.flightNumber}
                            </div>
                        </div>

                        {/* Arrival Information */}
                        <div className="arrival-info">
                            <div className="city-name">{arrival.airport.city}</div>
                            <div className="airport-code">{arrival.airport.fullDisplay}</div>
                            <div className="time">{arrival.datetime.time}</div>
                            <div className="date">{arrival.datetime.date === 'Not specified' ? 'No arrival date' : arrival.datetime.date}</div>
                            <div className="timezone">{arrival.timezone}</div>
                        </div>
                    </div>
                </div>

                {/* Related Documents moved to StandardDetailsLayout */}
            </div>

            {/* Booking Information */}
            <DetailSection title="Booking Information">
                <div className="details-grid">
                    {details.bookingReference && (
                        <div className="detail-item">
                            <div className="detail-label">Booking Reference</div>
                            <div className="detail-value">{details.bookingReference}</div>
                        </div>
                    )}

                    {cost && (
                        <div className="detail-item">
                            <div className="detail-label">Cost</div>
                            <div className="detail-value">
                                {formatCurrency(cost.amount, cost.currency) === 'Not specified' ? 'No cost information' : formatCurrency(cost.amount, cost.currency)}
                                <span className="payment-status">
                                    {cost.paid ? ' (Paid)' : ' (Unpaid)'}
                                </span>
                            </div>
                        </div>
                    )}

                    {details.bookingAgent && (
                        <div className="detail-item">
                            <div className="detail-label">Booked Through</div>
                            <div className="detail-value">{details.bookingAgent}</div>
                        </div>
                    )}

                    {details.classOfService && (
                        <div className="detail-item">
                            <div className="detail-label">Class</div>
                            <div className="detail-value">
                                {details.classOfService.charAt(0).toUpperCase() +
                                    details.classOfService.slice(1).replace('_', ' ')}
                            </div>
                        </div>
                    )}
                </div>

                {/* Ticket Numbers */}
                {Array.isArray(details.ticketNumbers) && details.ticketNumbers.length > 0 && (
                    <div className="ticket-numbers">
                        <div className="detail-label">Ticket Numbers</div>
                        <div className="ticket-list">
                            {details.ticketNumbers.map((ticket, index) => (
                                ticket ? (
                                    <div key={index} className="ticket-number">
                                        <FaTicketAlt /> {ticket}
                                    </div>
                                ) : null
                            ))}
                        </div>
                    </div>
                )}
            </DetailSection>

            {/* Passenger Information */}
            {hasPassengers && (
                <DetailSection title="Passenger Information">
                    <div className="passengers-list">
                        {details.passengers.map((passenger, index) => (
                            passenger && (passenger.firstName || passenger.lastName || passenger.seat) ? (
                                <div key={index} className="passenger-card">
                                    <h3 className="passenger-header">
                                        <FaUser /> Passenger {index + 1}
                                    </h3>
                                    <div className="passenger-details">
                                        {(passenger.firstName || passenger.lastName) && (
                                            <div className="passenger-field">
                                                <span className="field-label">Name:</span>
                                                <span className="field-value">
                                                    {`${passenger.firstName || ''} ${passenger.lastName || ''}`.trim()}
                                                </span>
                                            </div>
                                        )}

                                        {passenger.seat && (
                                            <div className="passenger-field">
                                                <span className="field-label">Seat:</span>
                                                <span className="field-value">{passenger.seat}</span>
                                            </div>
                                        )}

                                        {passenger.mealPreference && passenger.mealPreference !== 'none' && (
                                            <div className="passenger-field">
                                                <span className="field-label">Meal:</span>
                                                <span className="field-value">
                                                    {passenger.mealPreference.charAt(0).toUpperCase() +
                                                        passenger.mealPreference.slice(1)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null
                        ))}
                    </div>
                </DetailSection>
            )}

            {/* Additional Information */}
            {details.baggageAllowance && (
                <DetailSection title="Additional Information">
                    <div className="details-grid">
                        <div className="detail-item">
                            <div className="detail-label">
                                <FaSuitcase /> Baggage Allowance
                            </div>
                            <div className="detail-value">{details.baggageAllowance}</div>
                        </div>
                    </div>
                </DetailSection>
            )}
        </div>
    );
};

export default FlightDetails; 